import { createElement, lazy, useRef } from 'react';

/**
 * React.lazy for a suite module, with two additions.
 *
 * `Component.preload()` starts the download early (see preloadWhenIdle), so opening a module the
 * browser already fetched in the background is instant. It is instant for real: once the file has
 * arrived the component renders at once. A bare React.lazy would still suspend for one tick on its
 * first render, even with the file in cache, and that tick swaps the screen for the loading
 * fallback and back, which shows as a flicker.
 *
 * A failed download reloads the page once. After a deploy, a tab opened on the previous build
 * asks for chunk files the new build no longer has; a reload picks up the new build. If a reload
 * already happened in the last minute the error is passed on (RouteErrorBoundary shows it)
 * rather than reloading in a loop.
 */
const RELOAD_KEY = 'suite-chunk-reload-at';
const RELOAD_WINDOW_MS = 60 * 1000;

const reloadedRecently = () => {
  try {
    return Date.now() - Number(window.sessionStorage.getItem(RELOAD_KEY) || 0) < RELOAD_WINDOW_MS;
  } catch (_) {
    return true;
  }
};

export default function lazyModule(load) {
  // The component itself, once its file has arrived (by preload or by the first render).
  let loaded = null;
  const remember = (mod) => {
    if (mod && mod.default) loaded = mod.default;
    return mod;
  };

  const Lazy = lazy(() => load().then(remember).catch((error) => {
    if (reloadedRecently()) throw error;
    try {
      window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch (_) {
      throw error;
    }
    window.location.reload();
    return new Promise(() => {});
  }));

  function LazyModule(props) {
    // Chosen once per mount: switching from one to the other later would remount the page and lose its state.
    const Impl = useRef(loaded || Lazy).current;
    return createElement(Impl, props);
  }
  LazyModule.displayName = 'LazyModule';
  LazyModule.preload = () => load().then(remember).catch(() => {});
  return LazyModule;
}

/** Download the given modules once the browser is idle, one after another. */
export function preloadWhenIdle(modules) {
  const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500));
  let cancelled = false;
  const queue = [...modules];
  const next = () => {
    if (cancelled || !queue.length) return;
    queue.shift().preload().finally(() => idle(next));
  };
  const handle = idle(next);
  return () => {
    cancelled = true;
    if (window.cancelIdleCallback && typeof handle === 'number') window.cancelIdleCallback(handle);
  };
}
